import React, { useMemo, useState, useEffect } from 'react'
import {
  Autosuggest,
  DialogOverlay,
  IconButton,
  DialogOverlayFooter,
  DialogOverlayActions,
  Button,
  Paragraph,
  Separator,
  useOverlay,
  useStatusOverlay,
} from '@fs/zion-ui'
import axios, { useZionAxios } from '@fs/zion-axios'
import { sgBaseUrl, appPath } from '@fs/zion-config'
import { MenuClose } from '@fs/zion-icon'
import { MenuNewWindow, NoticeLoading } from '@fs/zion-icon/dist/cjs/icons'
import { gql, useMutation } from '@apollo/client'
import styles from './watermarking.styles'
import transl from './Watermarking.transl'
import fsOperationsCallHeader from '../../../../../../utils/fsOperationsCallHeader'

const BASE_URL = `${sgBaseUrl}/service/records/watermark/orchestration`
const WATERMARK_ENDPOINT = {
  watermarks: `${BASE_URL}/watermark/list`,
  previewWatermark: (watermarkId, apid) => `${BASE_URL}/preview/watermark/${watermarkId}/apid/${apid}`,
}

export const applyWatermarkMutation = gql`
  mutation ApplyWatermark($groupApids: [String], $watermarkId: String!) {
    dasToDas(groupApids: $groupApids, dgsSet: null, watermarkId: $watermarkId, expectedAction: "ADD_WATERMARK") {
      code
      success
      message
    }
  }
`

/**
 * ApplyWatermarkDialog component allows users to apply watermarks to the image group.
 *
 * NOTE: This component works similarly to the ApplyWatermarkDialog component in lightyear-react.
 *    If you make changes here, ensure they are also reflected in lightyear-react.
 */
export default function ApplyWatermarkDialog({ overlay, currentApid, groupId, hasLegacyWatermark }) {
  const [{ data: watermarks = [], loading: watermarksLoading }] = useZionAxios(WATERMARK_ENDPOINT.watermarks)
  const [applyWatermark, { loading: applyWatermarkLoading }] = useMutation(applyWatermarkMutation)
  const showStatusOverlay = useStatusOverlay()
  const legacyWatermarkWarningOverlay = useOverlay()
  const [selectedWatermark, setSelectedWatermark] = useState(null)
  const [previewImageURL, setPreviewImageURL] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [inputTextSearch, setInputTextSearch] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  const previewCache = useMemo(() => new Map(), [])

  // Cleanup object URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (previewImageURL) {
        URL.revokeObjectURL(previewImageURL)
      }
    }
  }, [previewImageURL])

  const suggestions = useMemo(
    () =>
      watermarks.map(({ watermarkId, name }) => ({
        key: watermarkId,
        primaryText: name,
      })),
    [watermarks]
  )

  const onWatermarkSelected = async (watermark) => {
    try {
      setPreviewLoading(true)
      const previewCacheKey = `${currentApid}-${watermark.key}`
      const selectedWatermarkData = watermarks.find((w) => w.watermarkId === watermark.key)
      setSelectedWatermark(selectedWatermarkData)

      // revoke existing object URL if it exists
      if (previewImageURL) {
        URL.revokeObjectURL(previewImageURL)
      }

      // Fetch the preview image for the selected watermark
      if (previewCache.has(previewCacheKey)) {
        setPreviewImageURL(URL.createObjectURL(previewCache.get(previewCacheKey)))
      } else {
        const previewData = await axios.get(WATERMARK_ENDPOINT.previewWatermark(watermark.key, currentApid), {
          responseType: 'blob',
        })
        previewCache.set(previewCacheKey, previewData.data)
        setPreviewImageURL(URL.createObjectURL(previewData.data))
      }
      setPreviewLoading(false)
      setErrorMessage('')
    } catch (error) {
      console.error('Error fetching watermark preview:', error)
      setPreviewLoading(false)
      setErrorMessage(transl.errorFetchingPreview())
      setPreviewImageURL(null)
      setSelectedWatermark(null)
    }
  }

  const removeSelectedWatermark = () => {
    if (previewImageURL) {
      URL.revokeObjectURL(previewImageURL)
    }
    setSelectedWatermark(null)
    setPreviewImageURL(null)
    setInputTextSearch('')
  }

  async function callApplyWatermark() {
    try {
      const result = await applyWatermark({
        variables: {
          groupApids: [groupId],
          watermarkId: selectedWatermark.watermarkId,
        },
        context: { headers: { ...fsOperationsCallHeader } },
      })

      if (result.data.dasToDas.success) {
        showStatusOverlay({
          message: transl.applyWatermarkSuccess(),
          type: 'success',
          dismissable: true,
        })
        overlay.close()
      } else {
        showStatusOverlay({
          message: transl.applyWatermarkFailure(result.data.dasToDas.message),
          type: 'error',
          dismissable: true,
        })
      }
    } catch (error) {
      showStatusOverlay({
        message: transl.applyWatermarkError(),
        type: 'error',
        dismissable: true,
      })
      console.error('Error applying watermark:', error)
    }
  }

  async function handleApplyWatermark() {
    if (hasLegacyWatermark) {
      legacyWatermarkWarningOverlay.open()
    } else {
      callApplyWatermark()
    }
  }

  return (
    <>
      <DialogOverlay {...overlay} title={transl.applyWatermark()}>
        <div>{transl.selectWatermarkMessage()}</div>
        <Autosuggest
          data-testid="searchWatermarkAutosuggest"
          Icon={watermarksLoading ? NoticeLoading : null}
          label=""
          placeholder={transl.searchExistingWatermarks()}
          autoComplete="on"
          suggestions={suggestions}
          onSuggestionSelected={onWatermarkSelected}
          disabled={!!selectedWatermark || watermarksLoading}
          errorMessage={errorMessage}
          inputText={inputTextSearch}
          onInputTextChange={(e) => setInputTextSearch(e)}
          clearable
        />
        {selectedWatermark && (
          <div css={styles.previewImage}>
            <div css={styles.previewTitle}>
              <span>
                {previewLoading ? (
                  <>
                    <NoticeLoading /> {transl.loading()}
                  </>
                ) : (
                  selectedWatermark.name
                )}
              </span>
              <IconButton
                data-testid="removeSelectedWatermark"
                Icon={MenuClose}
                color="gray"
                description={transl.removeSelectedWatermark()}
                onClick={removeSelectedWatermark}
              />
            </div>
            {previewImageURL && (
              <img src={previewImageURL} alt={transl.watermarkPreview()} style={{ width: '100%', height: 'auto' }} />
            )}
          </div>
        )}

        <Separator size="sm" />
        <span> {transl.watermarkingCondition()} </span>
        <Separator size="nano" />
        <WatermarkEditorButton />

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button data-testid="cancelApplyWatermarkingButton" emphasis="low" onClick={overlay.handleClick}>
              {transl.cancel()}
            </Button>
            <Button
              data-testid="sendRequestToApplyWatermarkingButton"
              Icon={applyWatermarkLoading ? NoticeLoading : null}
              emphasis="high"
              disabled={!selectedWatermark || applyWatermarkLoading}
              onClick={handleApplyWatermark}
            >
              {transl.apply()}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
      <HasLegacyWatermarkWarningDialog
        overlay={legacyWatermarkWarningOverlay}
        applyWatermark={callApplyWatermark}
        isApplying={applyWatermarkLoading}
      />
    </>
  )
}

function WatermarkEditorButton() {
  const url = `${appPath}/records/home/watermarking`

  return (
    <Button Icon={MenuNewWindow} iconDirection="backward" to={url} target="_blank">
      {transl.watermarkEditor()}
    </Button>
  )
}

function HasLegacyWatermarkWarningDialog({ overlay, applyWatermark, isApplying }) {
  return (
    <DialogOverlay {...overlay} title={transl.cautionExistingWatermarks()}>
      <Paragraph>{transl.legacyWatermarkParagraph1()}</Paragraph>
      <Paragraph>{transl.legacyWatermarkParagraph2()}</Paragraph>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            emphasis="low"
            onClick={overlay.handleClick}
            disabled={isApplying}
            data-testid="legacy-watermark-cancel-button"
          >
            {transl.cancel()}
          </Button>
          <Button
            Icon={isApplying ? NoticeLoading : null}
            emphasis="high"
            disabled={isApplying}
            onClick={applyWatermark}
            data-testid="legacy-watermark-continue-button"
          >
            {transl.continue()}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
